import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DropdownMenu, ErrorPanel, Icon, Skeleton, Typography } from "@/components/atoms";
import { DataTable, EmptyState, Pagination } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator, usePagination, useSorting } from "@/hooks";
import { useGetVehiclesQuery } from "@/redux/apis/vehicle/vehicleApi";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { getAssetUrl } from "@/utils";
import { archivedVehiclesColumns } from "../ArchivedVehicles/fixtures";
import { useGetArchivedVehiclesParams } from "../ArchivedVehicles/hooks/useGetArchivedVehiclesParams";
import { ReactivateVehicleModal } from "../ArchivedVehicles/modals/ReactivateVehicleModal";

const legacyColumns = archivedVehiclesColumns.filter((col) => col.id !== "vehicleType" && col.id !== "vehicleCategory");

export const LegacyArchivedVehiclesPanel = () => {
  const { id: operatorId } = useOperator();
  const { params } = useGetArchivedVehiclesParams();
  const { sorting, setSorting } = useSorting();
  const { handlePageClick, handleSizeChange } = usePagination("archived-vehicles", 25);
  const { isFetching, data: response, error } = useGetVehiclesQuery({ ...params, needs_type_migration: true }, { refetchOnMountOrArgChange: true });
  const [pendingReactivate, setPendingReactivate] = useState<Vehicle | undefined>();

  const data = useMemo(() => {
    if (!response) return [];
    return response.vehicles.map((vehicle) => ({
      numberPlate: (
        <Link to={`/operator/${operatorId}/vehicles-legacy/archived-vehicles/${vehicle.uuid}`} className="flex items-center gap-1 hover:text-info">
          {vehicle.registrationNumber}
        </Link>
      ),
      nickname: vehicle.identifier,
      make: (
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded bg-neutral-gray">
            <img
              src={getAssetUrl(vehicle.model.avatar, { width: 36, height: 36, fit: "contain", background: "transparent" })}
              alt={vehicle.model.make}
              className="h-[18px] w-[18px]"
            />
          </div>
          {vehicle.model.make}
        </div>
      ),
      model: vehicle.model.model,
      year: vehicle.year?.toString() ?? "-",
      vehicleType: <Typography className="line-clamp-1">{vehicle.vehicleType?.name ?? "-"}</Typography>,
      vehicleCategory: vehicle.vehicleCategory?.name ?? "-",
      actions: (
        <DropdownMenu button={<Icon name="options" isCustom size="lg" />} position="bottom-start" className="!flex max-w-[24px]">
          <Link to={`/operator/${operatorId}/vehicles-legacy/archived-vehicles/${vehicle.uuid}`}>
            <DropdownMenu.Item>
              <Icon name="ArrowRight" className="mr-2.5 flex-shrink-0 text-primary-dark" size="sm" />
              <Typography className="text-primary-dark">View Vehicle</Typography>
            </DropdownMenu.Item>
          </Link>
          <DropdownMenu.Item onClick={() => setPendingReactivate(vehicle)}>
            <Icon name="Refresh2" className="mr-2.5 flex-shrink-0 text-neutral-dark-gray" size="sm" />
            <div className="flex flex-col">
              <Typography className="text-primary-dark">Reactivate Vehicle</Typography>
              <Typography variant="small" className="text-neutral-silver-gray">
                Operator vehicles go back to My Vehicles, while driver vehicles are managed in the new Vehicle Manager and must be updated by the
                driver in the Driver App.
              </Typography>
            </div>
          </DropdownMenu.Item>
        </DropdownMenu>
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response?.vehicles, operatorId]);

  if (error) return <ErrorPanel messages={getErrorMessages(error)} />;
  if (isFetching) return <Skeleton />;

  if (response && response.vehicles.length > 0)
    return (
      <>
        <DataTable
          className="w-full overflow-x-auto [&_table]:table-auto [&_tr]:bg-white"
          data={data}
          columns={legacyColumns}
          sorting={sorting}
          setSorting={setSorting}
        />
        <Pagination
          onPageClick={handlePageClick}
          onSizeChange={handleSizeChange}
          currentPage={response.page.current}
          pageSize={response.page.size}
          totalItems={response.page.total}
          pageCount={response.page.count}
        />
        <ReactivateVehicleModal pendingReactivate={pendingReactivate} setPendingReactivate={setPendingReactivate} />
      </>
    );

  return <EmptyState title="No Archived Vehicles" description="There are no archived vehicles to display." />;
};
